import React, { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import API from '../lib/api';
import NewsDetailModal from './NewsDetailModal';

const News = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/news');
        setItems((data.items || []).slice(0, 3));
      } catch {}
    })();
  }, []);

  const openArticle = async (item) => {
    // Ensure we have the full doc (with `content`) even if the list endpoint trims it in the future.
    try {
      const { data } = await API.get(`/news/${item.id}`);
      setActive(data.news || item);
    } catch {
      setActive(item);
    }
  };

  if (items.length === 0) return null;

  return (
    <section id="news" className="px-4 md:px-10 py-20">
      <div className="mx-auto max-w-[1240px]">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-700/30 bg-emerald-100 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-800">Newsroom</div>
            <h2 className="mt-4 font-serif text-emerald-950 text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Latest from the <span className="italic text-emerald-600">skyline</span>.
            </h2>
          </div>
          <a href="#all-news" className="inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-600 text-sm font-medium">
            View all articles <ArrowUpRight size={16} />
          </a>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((n) => (
            <article
              key={n.id}
              onClick={() => openArticle(n)}
              className="group cursor-pointer rounded-2xl overflow-hidden border border-emerald-900/60 bg-[#07160e] hover:border-emerald-700/70 transition"
              data-testid={`news-card-${n.id}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={n.image} alt={n.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition duration-700" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest">
                  <span className="text-emerald-400">{n.category}</span>
                  <span className="text-emerald-100/40">•</span>
                  <span className="text-emerald-100/60">{n.date}</span>
                </div>
                <h3 className="mt-3 text-white text-lg font-medium leading-snug group-hover:text-emerald-100 transition">{n.title}</h3>
                <p className="mt-2 text-[14px] text-emerald-100/65 leading-relaxed line-clamp-2">{n.excerpt}</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openArticle(n); }}
                  className="mt-4 inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200 text-sm font-medium"
                  data-testid={`news-read-${n.id}`}
                >
                  Read article <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {active && <NewsDetailModal news={active} onClose={() => setActive(null)} />}
    </section>
  );
};

export default News;
